/**
 * Der Objektbildschirm ohne ausgewähltes Objekt.
 *
 * Am Schreibtisch ist der Baum in der Sidebar und diese Ansicht nur der
 * Hinweis, dass links etwas auszuwählen ist. Am Telefon gibt es keine Sidebar —
 * dort **ist** diese Ansicht derselbe Baum (`ObjectTree`, Spec 02a, aus
 * `PropertyTree` geworden), als Drill-down-Liste, genau wie in der iOS-App.
 *
 * Genau eine der beiden Darstellungen steht im Dokument (Spec 01, „Der
 * Baum"): `useIsDesktopViewport` entscheidet in JavaScript, nicht nur per
 * CSS — sonst gäbe es `property-add` und `property-row-<id>` doppelt, weil
 * die Sidebar in `router.tsx` auf schmalen Bildschirmen ohnehin nicht
 * mountet.
 *
 * `PropertySheet` mountet hier statt in `ObjectTree` selbst: Der Baum liegt
 * in `features/objects` und kennt `features/properties` nicht — komponiert
 * wird in der Route (`apps/web/CLAUDE.md`, „features kennen einander nicht").
 * Zielt ein Archivieren ohne Elternobjekt hierher (Spec 02a, „Für
 * Grundstücke bleibt … Ziel ist /objekt"), steht auch der Hinweisstreifen
 * hier.
 */

import { useRouterState } from '@tanstack/react-router'
import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ObjectArchivedNotice } from '@/features/objects/ObjectArchivedNotice'
import { ObjectTree } from '@/features/objects/ObjectTree'
import { PrivateAreaSheet } from '@/features/objects/PrivateAreaSheet'
import { PropertySheet } from '@/features/properties/PropertySheet'
import { useIsDesktopViewport } from '@/lib/viewport'

export function ObjectList() {
  const isDesktop = useIsDesktopViewport()
  const archivedNotice = useRouterState({ select: (state) => state.location.state.archivedNotice })
  const [isPropertySheetOpen, setIsPropertySheetOpen] = useState(false)
  const [isPrivateAreaSheetOpen, setIsPrivateAreaSheetOpen] = useState(false)

  return (
    <section aria-labelledby="objects-heading" data-testid="screen-objects" className="flex h-full flex-col">
      {/* Am Schreibtisch trägt die Kontext-Sidebar bereits „Objekte" (ADR 0011,
       * `ContextSidebar title="Objekte"`). Ein zweiter Kopf daneben sagte
       * dasselbe Wort zweimal in einer Zeile — derselbe Befund, der in
       * `Geld.tsx` schon behoben ist, dort gelöst über den Namen der
       * *Ansicht* statt des Bereichs. Hier gibt es keine Ansicht: Der
       * Bildschirm ist der Hinweis, links etwas zu wählen, und das sagt der
       * Leerzustand darunter bereits vollständig.
       *
       * Die Überschrift bleibt deshalb im Dokument, aber nur für Screenreader
       * — `aria-labelledby` braucht sie, und ohne sie hätte der Abschnitt
       * keinen zugänglichen Namen. Am Telefon gibt es keine Sidebar; dort ist
       * „Objekte" der einzige Ort, an dem der Bereich benannt wird, und der
       * Kopf steht sichtbar. */}
      {isDesktop ? (
        <h1 id="objects-heading" className="sr-only">
          Objekte
        </h1>
      ) : (
        <header className="border-border flex h-(--row-h) shrink-0 items-center border-b px-(--page-x)">
          <h1 id="objects-heading" className="text-page font-(--font-weight-page)">
            Objekte
          </h1>
        </header>
      )}

      <ObjectArchivedNotice notice={archivedNotice} />

      {isDesktop ? (
        <EmptyState
          testId="objects-empty"
          title="Wähle ein Objekt in der Seitenleiste."
          hint="Grundstücke, Gebäude und Einheiten bilden den Baum, über den alles andere erreichbar ist."
        />
      ) : (
        <ObjectTree
          className="flex-1"
          onAddProperty={() => {
            setIsPropertySheetOpen(true)
          }}
          onAddPrivateArea={() => {
            setIsPrivateAreaSheetOpen(true)
          }}
        />
      )}

      <PropertySheet open={isPropertySheetOpen} onOpenChange={setIsPropertySheetOpen} />
      <PrivateAreaSheet open={isPrivateAreaSheetOpen} onOpenChange={setIsPrivateAreaSheetOpen} />
    </section>
  )
}
